import { useCallback } from 'react';
import { deletePost, archivePost, unarchivePost } from '../services/api';
import toast from 'react-hot-toast';
import {
  trackPostArchived,
  trackPostUnarchived,
  trackPostDeleted,
  trackPostShared,
  trackPostFavourited,
  trackPostUnfavourited
} from '../utils/eventTracking';
import { getShareText } from '../lib/postUtils';
import { Post } from '../types';

interface UsePostActionsProps {
  onPostDelete?: (postId: number) => void;
  onPostArchive?: (postId: number) => void;
  onFavouriteToggle?: (postId: number, isFavourite: boolean) => void;
  favouriteStates?: { [key: string]: boolean };
  onGlobalLoadingChange?: (isLoading: boolean, message?: string) => void;
}

export const usePostActions = ({
  onPostDelete,
  onPostArchive,
  onFavouriteToggle,
  favouriteStates,
  onGlobalLoadingChange
}: UsePostActionsProps) => {
  const handleDeletePost = useCallback(async (postId: number, platform: string) => {
    onGlobalLoadingChange?.(true, 'Deleting post...');
    try {
      await deletePost(postId);
      toast.success('Post deleted successfully');
      trackPostDeleted(postId.toString(), platform);
      onPostDelete?.(postId);
    } catch (error) {
      console.error('Failed to delete post:', error);
      toast.error('Failed to delete post. Please try again.');
    } finally {
      onGlobalLoadingChange?.(false);
    }
  }, [onPostDelete, onGlobalLoadingChange]);

  const handleConfirmArchive = useCallback(async (postId: number, platform: string) => {
    onGlobalLoadingChange?.(true, 'Archiving post...');
    try {
      await archivePost(postId);
      toast.success('Post archived successfully');
      trackPostArchived(postId.toString(), platform, "Delete Dialog");
      onPostArchive?.(postId);
    } catch (error) {
      console.error('Failed to archive post:', error);
      toast.error('Failed to archive post. Please try again.');
    } finally {
      onGlobalLoadingChange?.(false);
    }
  }, [onPostArchive, onGlobalLoadingChange]);

  const handleArchivePost = useCallback(async (post: Post) => {
    const message = post.is_archived ? 'Unarchiving post...' : 'Archiving post...';
    onGlobalLoadingChange?.(true, message);
    try {
      if (post.is_archived) {
        await unarchivePost(post.id);
        toast.success('Post unarchived successfully');
        trackPostUnarchived(post.id.toString(), post.platform);
        onPostArchive?.(post.id);
      } else {
        await archivePost(post.id);
        toast.success('Post archived successfully');
        trackPostArchived(post.id.toString(), post.platform, "Post Menu");
        onPostArchive?.(post.id);
      }
    } catch (error) {
      console.error('Failed to archive/unarchive post:', error);
      toast.error('Failed to update post. Please try again.');
    } finally {
      onGlobalLoadingChange?.(false);
    }
  }, [onPostArchive, onGlobalLoadingChange]);

  const handleSharePost = useCallback(async (post: Post) => {
    try {
      const shareText = getShareText(post);
      await navigator.clipboard.writeText(shareText);
      toast.success('Post link copied to clipboard!');
      trackPostShared(post.id.toString(), post.platform);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      toast.error('Failed to copy link. Please try again.');
    }
  }, []);

  const handleFavouriteToggle = useCallback(async (post: Post) => {
    if (onFavouriteToggle) {
      const currentFavouriteState = favouriteStates?.[post.id.toString()] ?? post.is_favorite ?? false;
      const newFavouriteState = !currentFavouriteState;
      
      if (newFavouriteState) {
        trackPostFavourited(post.id.toString(), post.platform);
      } else {
        trackPostUnfavourited(post.id.toString(), post.platform);
      }
      
      await onFavouriteToggle(post.id, newFavouriteState);
    }
  }, [onFavouriteToggle, favouriteStates]);

  return {
    handleDeletePost,
    handleConfirmArchive,
    handleArchivePost,
    handleSharePost,
    handleFavouriteToggle
  };
};
